import React, { useState, useMemo, FC } from "react";
import { useTranslation } from "react-i18next";
import { RoundButton, Text, Heading, CardNotification } from "@foris/avocado-suite";
import { Header } from "@common/components";
import { useHistory } from "react-router-dom";
import useContextUrl from "@common/hooks/useContextUrl";
import useGetCatalogs from "@common/hooks/useGetCatalogs";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import Layout from "@common/layout/Layout";
import CatalogsSkeleton from "./ReportsList.skeleton";
import ResourceList from "@modules/common/resource-list";
import ResourceFilter from "@modules/common/resource-filter";
import ResourceSearch from "@modules/common/resource-search/ResourceSearch";
import styles from "./reportsList.module.scss";

const ReportsList: FC = () => {
  const { t } = useTranslation();
  const { workspace, scenario, origin } = useContextUrl();

  const history = useHistory();

  const { homeUrl, adaptedHomeUrl } = useNavigationUrl();
  const { catalogsOptions, hasAccessToCatalogs, isLoading } = useGetCatalogs();
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCatalogs = useMemo(() => {
    return catalogsOptions.filter(catalog => {
      const matchesCategory = !selectedFilter || catalog.self.category === selectedFilter;
      const matchesSearch =
        !searchQuery || catalog.self.label.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [catalogsOptions, selectedFilter, searchQuery]);

  if (isLoading) {
    return (
      <>
        {hasAccessToCatalogs && (
          <Layout contextSearch={false}>
            <Header
              className={styles.header}
              title={t("report.title")}
              breadcrumbItems={[
                {
                  title: t("report.breadcrumb.prev"),
                  path: adaptedHomeUrl(),
                },
                {
                  title: t("report.breadcrumb.current"),
                },
              ]}
              subTitle={<Text darkMode>{t("report.subtitle")}</Text>}
            />
            <CatalogsSkeleton items={catalogsOptions} />
          </Layout>
        )}
      </>
    );
  }

  return (
    <>
      {hasAccessToCatalogs && (
        <Layout contextSearch={false}>
          <Header
            className={styles.header}
            title={t("report.title")}
            breadcrumbItems={[
              {
                title: t("report.breadcrumb.prev"),
                path: adaptedHomeUrl(),
              },
              {
                title: t("report.breadcrumb.current"),
              },
            ]}
            subTitle={<Text darkMode>{t("report.subtitle")}</Text>}
          />
          <main className={styles.reports}>
            <div className={styles.reports_header}>
              <RoundButton
                onClick={() => history.push(homeUrl(workspace, scenario, origin))}
                variant="ghost"
                icon="chevron-left"
              />
              <Heading type="h2">
                {filteredCatalogs?.length ?? 0}{" "}
                {filteredCatalogs?.length === 1
                  ? t("report.back-title-single")
                  : t("report.back-title")}
              </Heading>
            </div>
            <ResourceSearch
              placeholder={t("report.input-search.placeholder")}
              onSearch={setSearchQuery}
            />
            <ResourceFilter
              data={(catalogsOptions ?? [])?.map(option => option.self)}
              selectedResource={selectedFilter}
              searchQuery={searchQuery}
              onFilterChange={setSelectedFilter}
              label={t("report.filter-options.all")}
            />
            {filteredCatalogs?.length > 0 ? (
              <ResourceList resources={filteredCatalogs} />
            ) : (
              <CardNotification
                className={styles.reports_cardNotification}
                outlined
                state="info"
                title={t("report.card-notification.title")}
              >
                {t("report.card-notification.text")}
              </CardNotification>
            )}
          </main>
        </Layout>
      )}
    </>
  );
};

export default ReportsList;
